import React, { useState, useEffect, useMemo } from "react";
import {
  Users, ShieldAlert, UserCheck,
  AlertTriangle, CheckCircle, AlertOctagon,
  Clock, MapPin, RefreshCw, Wifi, Camera, Eye, TrendingUp, Activity, BarChart3, Calendar
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie, Legend
} from "recharts";
import { Card, Statistic, Row, Col, Tag, Badge, Button, Tabs, Space, Typography, Divider, Table, Avatar, DatePicker } from "antd";
import dayjs from "dayjs";
import axios from "../../utils/axiosDash";

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

// ── THEME COLORS ──────────────────────────────────────────────────────────
const COLORS = {
  primary: "#3b82f6",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  purple: "#8b5cf6",
  pink: "#ec4899",
  cyan: "#06b6d4",
  orange: "#f97316",
  dark: "#0f172a",
  gray: "#64748b",
  lightGray: "#f1f5f9"
};

const T = {
  critical: { bg: "#fef2f2", border: "#fecaca", text: "#dc2626", label: "Critical", Icon: AlertOctagon },
  warning:  { bg: "#fffbeb", border: "#fde68a", text: "#d97706", label: "Warning",  Icon: AlertTriangle },
  ok:       { bg: "#f0fdf4", border: "#bbf7d0", text: "#16a34a", label: "OK",       Icon: CheckCircle },
};

// ── CUSTOM TOOLTIP ───────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, unit = "" }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#fff", border: "1px solid #e2e8f0",
      borderRadius: 12, padding: "10px 16px", fontSize: 12,
      boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
    }}>
      <p style={{ fontWeight: 700, color: "#334155", marginBottom: 6 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill, margin: "3px 0" }}>
          {p.name}: <strong>{p.value}{unit}</strong>
        </p>
      ))}
    </div>
  );
};

// ── ALERT ROW COMPONENT ─────────────────────────────────────────────────
const AlertRow = ({ alert, showPersons = false, last = false }) => {
  const s = T[alert.level];
  const Icon = s.Icon;
  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "14px 16px",
        borderBottom: last ? "none" : "1px solid #f1f5f9",
        transition: "background 0.15s",
      }}
      className="alert-row"
    >
      <div style={{
        width: 32, height: 32, borderRadius: 10, flexShrink: 0,
        background: s.bg, display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={16} color={s.text} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>
          {alert.title}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <MapPin size={10} color="#94a3b8" />
            <span style={{ fontSize: 11, color: "#94a3b8" }}>{alert.location}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Clock size={10} color="#94a3b8" />
            <span style={{ fontSize: 11, color: "#94a3b8" }}>{alert.time}</span>
          </div>
        </div>
      </div>
      {showPersons && alert.persons > 0 && (
        <div style={{
          display: "flex", alignItems: "center", gap: 5,
          background: "#eff6ff", borderRadius: 8, padding: "4px 12px",
        }}>
          <Users size={12} color="#3b82f6" />
          <span style={{ fontSize: 12, fontWeight: 700, color: "#1d4ed8" }}>{alert.persons}</span>
        </div>
      )}
      <Tag color={s.text} style={{ borderRadius: 40, fontSize: 10, fontWeight: 700, border: 'none', background: s.bg, color: s.text, padding: "2px 12px" }}>
        {s.label}
      </Tag>
    </div>
  );
};

// ── STAT CARD COMPONENT ──
const StatCard = ({ title, value, icon: Icon, color, subtitle, trend }) => (
  <Card style={{ borderRadius: 20, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }} bodyStyle={{ padding: "20px" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div>
        <Text type="secondary" style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase" }}>{title}</Text>
        <div style={{ fontSize: 32, fontWeight: 800, color: COLORS.dark, marginTop: 8, marginBottom: 4 }}>{value}</div>
        {subtitle && <Text style={{ fontSize: 11, color: COLORS.gray }}>{subtitle}</Text>}
        {trend && <Tag color="success" style={{ marginTop: 8, fontSize: 10 }}>{trend}</Tag>}
      </div>
      <div style={{
        width: 48, height: 48, borderRadius: 24,
        background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center"
      }}>
        <Icon size={24} color={color} />
      </div>
    </div>
  </Card>
);

// ── DATA PROCESSING WITH DATE FILTER ───────────────────────────────────────────
function processRawData(rawData, startDate, endDate) {
  // Apply date filter if dates are selected
  let filtered = [...rawData];
  
  if (startDate && endDate) {
    filtered = rawData.filter(item => {
      const itemDate = dayjs(item.timestamp);

      return (
        itemDate.valueOf() >= startDate.startOf("day").valueOf() &&
        itemDate.valueOf() <= endDate.endOf("day").valueOf()
      );
    });
  }

  // CORRECTED: Group by zoneType, not eventType
  const intrusionEvents = filtered.filter(item => item.zoneType === "Intrusion");
  const tailgatingEvents = filtered.filter(item => item.zoneType === "Tailgating");
  const overcrowdingEvents = filtered.filter(item => item.zoneType === "Head Count");
  
  // Location based analytics using zoneType
  const locationStats = {};
  filtered.forEach(item => {
    if (!locationStats[item.location]) {
      locationStats[item.location] = { intrusions: 0, tailgating: 0, overcrowding: 0, total: 0 };
    }
    if (item.zoneType === "Intrusion") locationStats[item.location].intrusions++;
    if (item.zoneType === "Tailgating") locationStats[item.location].tailgating++;
    if (item.zoneType === "Head Count") locationStats[item.location].overcrowding++;
    locationStats[item.location].total++;
  });

  // Hourly trend data based on zoneType
  const hourlyData = {};
  filtered.forEach(item => {
    const hour = dayjs(item.timestamp).format("HH:00");
    if (!hourlyData[hour]) {
      hourlyData[hour] = { hour, intrusions: 0, tailgating: 0, overcrowding: 0, total: 0 };
    }
    if (item.zoneType === "Intrusion") hourlyData[hour].intrusions++;
    if (item.zoneType === "Tailgating") hourlyData[hour].tailgating++;
    if (item.zoneType === "Head Count") hourlyData[hour].overcrowding++;
    hourlyData[hour].total++;
  });
  const hourlyTrend = Object.values(hourlyData).sort((a,b) => a.hour.localeCompare(b.hour));

  // Event type distribution based on zoneType
  const eventDistribution = [
    { name: "Intrusions", value: intrusionEvents.length, color: COLORS.danger },
    { name: "Tailgating", value: tailgatingEvents.length, color: COLORS.warning },
    { name: "Head Count/Overcrowding", value: overcrowdingEvents.length, color: COLORS.primary },
  ];

  // Person count data from details (only for Head Count zoneType)
  const personData = filtered
    .filter(item => item.zoneType === "Head Count" && item.details?.total_persons)
    .map(item => ({
      time: dayjs(item.timestamp).format("HH:mm"),
      persons: item.details.total_persons,
      location: item.location,
      zoneType: item.zoneType,
      kitchen: item.details.kitchen_persons,
      sofa1: item.details.sofa1_persons,
      sofa2: item.details.sofa2_persons,
      sofa3: item.details.sofa3_persons
    }));

  // Camera status analytics
  const onlineCameras = filtered.filter(item => item.cameraStatus === "Online").length;
  const uniqueCameras = [...new Set(filtered.map(item => item.cameraName))];
  const uniqueLocations = [...new Set(filtered.map(item => item.location))];

  // Summary
  const totalEvents = filtered.length;
  const avgPersons = personData.length > 0 
    ? Math.round(personData.reduce((sum, d) => sum + d.persons, 0) / personData.length) 
    : 0;

  // Recent alerts based on actual events
  const recentAlerts = filtered
    .sort((a,b) => new Date(b.timestamp) - new Date(a.timestamp))
    .slice(0, 10)
    .map(item => {
      let level = "ok";
      let title = "";
      if (item.zoneType === "Intrusion") {
        level = "critical";
        title = "Intrusion detected in restricted area";
      } else if (item.zoneType === "Tailgating") {
        level = "warning";
        title = "Tailgating event - unauthorized entry";
      } else if (item.zoneType === "Head Count") {
        level = "warning";
        title = `Overcrowding alert - ${item.details?.total_persons || 0} persons detected`;
      }
      return {
        level,
        title,
        location: item.location,
        time: dayjs(item.timestamp).format("HH:mm:ss"),
        persons: item.details?.total_persons || 0,
        isIntrusion: item.zoneType === "Intrusion",
      };
    });

  return {
    intrusionEvents: intrusionEvents.length,
    tailgatingEvents: tailgatingEvents.length,
    overcrowdingEvents: overcrowdingEvents.length,
    totalEvents,
    uniqueLocations: uniqueLocations.length,
    uniqueCameras: uniqueCameras.length,
    onlineCameras,
    avgPersons,
    hourlyTrend,
    eventDistribution,
    personData,
    recentAlerts,
    locationStats,
    rawFiltered: filtered,
    dateRange: { startDate, endDate }
  };
}

// ── MAIN DASHBOARD ──────────────────────────────────────────────────────
export default function Dashboard() {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [dateRange, setDateRange] = useState([null, null]);

  const data = useMemo(
    () => processRawData(rawData, dateRange[0], dateRange[1]),
    [rawData, dateRange]
  );

  const fetchTablespace = async () => {
    try {
      setLoading(true);
      const response = await axios.post("/fetch-tablespace");
      if (response.data.success) {
        setRawData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching tablespace data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTablespace();
  }, []);

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setDateRange(dates || [null, null]);
  };

  // Clear date filter
  const clearDateFilter = () => {
    setDateRange([null, null]);
  };

  // Columns for location table
  const locationColumns = [
    { title: 'Location', dataIndex: 'location', key: 'location', render: (text) => <strong>{text}</strong> },
    { title: 'Intrusions', dataIndex: 'intrusions', key: 'intrusions', render: (val) => <Tag color="error">{val}</Tag> },
    { title: 'Tailgating', dataIndex: 'tailgating', key: 'tailgating', render: (val) => <Tag color="warning">{val}</Tag> },
    { title: 'Head Count/Overcrowding', dataIndex: 'overcrowding', key: 'overcrowding', render: (val) => <Tag color="processing">{val}</Tag> },
    { title: 'Total Events', dataIndex: 'total', key: 'total', render: (val) => <strong style={{ color: COLORS.primary }}>{val}</strong> },
  ];

  const locationData = Object.entries(data.locationStats).map(([location, stats]) => ({
    location,
    ...stats
  }));

  // Get date range display text
  const getDateRangeText = () => {
    if (dateRange[0] && dateRange[1]) {
      return `${dateRange[0].format('YYYY-MM-DD')} to ${dateRange[1].format('YYYY-MM-DD')}`;
    }
    return "All Data";
  };

  return (
    <div style={{ padding: "24px 32px", background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
      <div style={{ maxWidth: 1600, margin: "0 auto" }}>

        {/* ── HEADER ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: COLORS.primary, marginBottom: 6 }}>
              AI Security Analytics
            </div>
            <Title level={2} style={{ margin: 0, fontWeight: 800, letterSpacing: "-0.5px" }}>
              Video Analytics Dashboard
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Real-time monitoring · {data.uniqueCameras} cameras · {data.uniqueLocations} zones
            </Text>
            {/* <div style={{ marginTop: 8 }}>
              <Tag color="blue" icon={<Calendar size={12} />} style={{ borderRadius: 12 }}>
                {getDateRangeText()}
              </Tag>
            </div> */}
          </div>
          <Space size="middle" direction="vertical" align="end">
            <Space size="middle">
              <RangePicker
                onChange={handleDateRangeChange}
                value={dateRange}
                placeholder={['Start Date', 'End Date']}
                format="YYYY-MM-DD"
                style={{ borderRadius: 40, height: 40 }}
                allowClear={true}
              />
              <Button
                onClick={clearDateFilter}
                style={{ borderRadius: 40, height: 40 }}
                disabled={!dateRange[0] && !dateRange[1]}
              >
                Clear Filter
              </Button>
              <Button
                onClick={fetchTablespace}
                loading={loading}
                icon={<RefreshCw size={16} />}
                style={{ borderRadius: 40, height: 40, borderColor: "#e2e8f0" }}
              >
                Refresh
              </Button>
              <Badge status="success" text={<span style={{ fontSize: 12, fontWeight: 500 }}>Live</span>} />
            </Space>
            {dateRange[0] && dateRange[1] && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Showing data from {dateRange[0].format('MMMM D, YYYY')} to {dateRange[1].format('MMMM D, YYYY')}
              </Text>
            )}
          </Space>
        </div>

        {/* ── KPI CARDS - CORRECTED COUNTS ── */}
        <Row gutter={[16, 16]} style={{ marginBottom: 28 }}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard title="Total Security Events" value={data.totalEvents} icon={AlertTriangle} color={COLORS.danger} subtitle="All incidents detected" />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard title="Intrusions" value={data.intrusionEvents} icon={ShieldAlert} color={COLORS.danger} subtitle="Unauthorized access" />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard title="Tailgating" value={data.tailgatingEvents} icon={AlertTriangle} color={COLORS.warning} subtitle="Door following attempts" />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard title="Head Count Events" value={data.overcrowdingEvents} icon={Users} color={COLORS.primary} subtitle="Occupancy monitoring" />
          </Col>
        </Row>

        {/* ── TABS FOR ANALYTICS ── */}
        <Card style={{ borderRadius: 20, border: "1px solid #e2e8f0", marginBottom: 28 }} bodyStyle={{ padding: 0 }}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: "overview",
                label: <span><BarChart3 size={14} style={{ marginRight: 8 }} />Overview Analytics</span>,
                children: (
                  <div style={{ padding: "20px 24px" }}>
                    <Row gutter={[24, 24]}>
                      {/* Event Distribution Pie Chart */}
                      <Col xs={24} lg={12}>
                        <div style={{ background: "#f9fafb", borderRadius: 16, padding: "16px" }}>
                          <Text strong style={{ fontSize: 14, display: "block", marginBottom: 16 }}>Event Distribution by Zone Type</Text>
                          <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                              <Pie
                                data={data.eventDistribution}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={90}
                                paddingAngle={3}
                                dataKey="value"
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                labelLine={{ strokeWidth: 1 }}
                              >
                                {data.eventDistribution.map((entry, idx) => (
                                  <Cell key={idx} fill={entry.color} stroke="white" strokeWidth={2} />
                                ))}
                              </Pie>
                              <Tooltip content={<CustomTooltip />} />
                              <Legend verticalAlign="bottom" height={36} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </Col>

                      {/* Hourly Trend Area Chart */}
                      <Col xs={24} lg={12}>
                        <div style={{ background: "#f9fafb", borderRadius: 16, padding: "16px" }}>
                          <Text strong style={{ fontSize: 14, display: "block", marginBottom: 16 }}>Hourly Event Trends by Zone</Text>
                          <ResponsiveContainer width="100%" height={280}>
                            <AreaChart data={data.hourlyTrend}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="hour" tick={{ fontSize: 10 }} tickLine={false} />
                              <YAxis tick={{ fontSize: 10 }} tickLine={false} />
                              <Tooltip content={<CustomTooltip />} />
                              <Area type="monotone" dataKey="intrusions" name="Intrusions" stackId="1" stroke={COLORS.danger} fill={COLORS.danger} fillOpacity={0.6} />
                              <Area type="monotone" dataKey="tailgating" name="Tailgating" stackId="1" stroke={COLORS.warning} fill={COLORS.warning} fillOpacity={0.6} />
                              <Area type="monotone" dataKey="overcrowding" name="Head Count" stackId="1" stroke={COLORS.primary} fill={COLORS.primary} fillOpacity={0.6} />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </Col>

                      {/* Person Count Bar Chart */}
                      <Col xs={24} lg={12}>
                        <div style={{ background: "#f9fafb", borderRadius: 16, padding: "16px" }}>
                          <Text strong style={{ fontSize: 14, display: "block", marginBottom: 16 }}>Occupancy Data (Head Count Events)</Text>
                          <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={data.personData.slice(0, 15)}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                              <XAxis dataKey="time" tick={{ fontSize: 10 }} tickLine={false} />
                              <YAxis tick={{ fontSize: 10 }} tickLine={false} />
                              <Tooltip content={<CustomTooltip unit=" persons" />} />
                              <Bar dataKey="persons" name="Total Persons" radius={[6, 6, 0, 0]} fill={COLORS.primary} fillOpacity={0.85} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </Col>

                      {/* Average Metrics */}
                      <Col xs={24} lg={12}>
                        <div style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", borderRadius: 16, padding: "24px", color: "white" }}>
                          <Text style={{ color: "rgba(255,255,255,0.8)", fontSize: 13, display: "block", marginBottom: 20 }}>KEY METRICS</Text>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                            <div>
                              <div style={{ fontSize: 28, fontWeight: 800 }}>{data.avgPersons}</div>
                              <div style={{ fontSize: 11, opacity: 0.8 }}>Avg Persons per Event</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 28, fontWeight: 800 }}>{data.uniqueCameras}</div>
                              <div style={{ fontSize: 11, opacity: 0.8 }}>Active Cameras</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 28, fontWeight: 800 }}>{data.onlineCameras}</div>
                              <div style={{ fontSize: 11, opacity: 0.8 }}>Online Status</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 28, fontWeight: 800 }}>{((data.intrusionEvents / (data.totalEvents || 1)) * 100).toFixed(0)}%</div>
                              <div style={{ fontSize: 11, opacity: 0.8 }}>Intrusion Rate</div>
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                )
              },
              {
                key: "locations",
                label: <span><MapPin size={14} style={{ marginRight: 8 }} />Location Intelligence</span>,
                children: (
                  <div style={{ padding: "20px 24px" }}>
                    <Row gutter={[24, 24]}>
                      <Col xs={24}>
                        <div style={{ background: "#f9fafb", borderRadius: 16, padding: "16px" }}>
                          <Text strong style={{ fontSize: 14, display: "block", marginBottom: 16 }}>Security Events by Location (by Zone Type)</Text>
                          <Table
                            dataSource={locationData}
                            columns={locationColumns}
                            rowKey="location"
                            pagination={{ pageSize: 5 }}
                            size="middle"
                            bordered={false}
                            className="location-table"
                          />
                        </div>
                      </Col>
                      <Col xs={24} lg={12}>
                        <div style={{ background: "#f9fafb", borderRadius: 16, padding: "16px" }}>
                          <Text strong style={{ fontSize: 14, display: "block", marginBottom: 16 }}>Location Risk Score</Text>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={locationData} layout="vertical">
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                              <XAxis type="number" tick={{ fontSize: 10 }} />
                              <YAxis type="category" dataKey="location" tick={{ fontSize: 10 }} width={120} />
                              <Tooltip content={<CustomTooltip />} />
                              <Bar dataKey="total" name="Total Events" radius={[0, 6, 6, 0]} fill={COLORS.danger} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </Col>
                      <Col xs={24} lg={12}>
                        <div style={{ background: "#f9fafb", borderRadius: 16, padding: "16px" }}>
                          <Text strong style={{ fontSize: 14, display: "block", marginBottom: 16 }}>Zone Type Distribution by Location</Text>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={locationData}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                              <XAxis dataKey="location" tick={{ fontSize: 10, angle: -45, textAnchor: "end" }} height={60} />
                              <YAxis tick={{ fontSize: 10 }} />
                              <Tooltip content={<CustomTooltip />} />
                              <Legend />
                              <Bar dataKey="intrusions" name="Intrusions" fill={COLORS.danger} radius={[4, 4, 0, 0]} />
                              <Bar dataKey="tailgating" name="Tailgating" fill={COLORS.warning} radius={[4, 4, 0, 0]} />
                              <Bar dataKey="overcrowding" name="Head Count" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </Col>
                    </Row>
                  </div>
                )
              },
              {
                key: "alerts",
                label: <span><AlertOctagon size={14} style={{ marginRight: 8 }} />Recent Alerts</span>,
                children: (
                  <div style={{ padding: "20px 24px" }}>
                    <Row gutter={[24, 24]}>
                      <Col xs={24}>
                        <Card style={{ borderRadius: 16, background: "#f9fafb" }} bodyStyle={{ padding: 0 }}>
                          <div style={{ padding: "16px 20px", borderBottom: "1px solid #e2e8f0" }}>
                            <Text strong style={{ fontSize: 14 }}>Recent Security Alerts by Zone Type</Text>
                            <Text type="secondary" style={{ fontSize: 11, marginLeft: 12 }}>Last 10 events</Text>
                          </div>
                          <div>
                            {data.recentAlerts.length === 0 ? (
                              <div style={{ padding: "60px", textAlign: "center", color: COLORS.gray }}>
                                <CheckCircle size={48} color={COLORS.success} style={{ marginBottom: 16 }} />
                                <Text>No security events detected in the selected date range</Text>
                              </div>
                            ) : (
                              data.recentAlerts.map((alert, idx) => (
                                <AlertRow key={idx} alert={alert} showPersons last={idx === data.recentAlerts.length - 1} />
                              ))
                            )}
                          </div>
                        </Card>
                      </Col>
                    </Row>
                  </div>
                )
              }
            ]}
          />
        </Card>

        <style>{`
          .alert-row:hover {
            background: #f1f5f9;
            cursor: pointer;
          }
          .location-table .ant-table-thead > tr > th {
            background: #f8fafc;
            font-weight: 600;
            font-size: 12px;
          }
          .ant-tabs-tab {
            padding: 12px 20px !important;
          }
          .ant-tabs-tab-active {
            font-weight: 600;
          }
        `}</style>
      </div>
    </div>
  );
}